import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import {
  ConsolidatedForecastCreateData,
  ConsolidatedForecastUpdateData
} from '../../../../predict/src/core/api/types/consolidated-forecasting';
import { consolidatedForecastsQuery } from '../../../queries/consolidatedForecasts';
import {
  createConsolidatedForecastMutation,
  deleteConsolidatedForecastMutation,
  updateConsolidatedForecastMutation
} from '../../../mutations/consolidatedForecasts';

export interface ModifyConsolidatedForecastViewState extends ApiViewState {
  errorDeleting: Error | null;
  errorDuplicating: Error | null;
  errorUpdating: Error | null;
  forecast?: ConsolidatedForecastListItem;
  isDeleting: boolean;
  isDuplicating: boolean;
  isUpdating: boolean;
  newForecastId?: string;
}

export class ModifyConsolidatedForecastView extends ApiView {
  private consolidatedForecastsObs: ReturnType<typeof consolidatedForecastsQuery>;

  private deleteMutation: ReturnType<typeof deleteConsolidatedForecastMutation>;
  private duplicateMutation: ReturnType<typeof createConsolidatedForecastMutation>;
  private updateMutation: ReturnType<typeof updateConsolidatedForecastMutation>;

  public state: ModifyConsolidatedForecastViewState = {
    ...getDefaultApiViewState(),
    errorDeleting: null,
    errorDuplicating: null,
    errorUpdating: null,
    forecast: undefined,
    isDeleting: false,
    isDuplicating: false,
    isUpdating: false,
    newForecastId: undefined
  };

  constructor(private workspaceId: string, private consolidationId: string, private forecastId: string) {
    super();

    this.consolidatedForecastsObs = this.addQuery(consolidatedForecastsQuery(workspaceId, consolidationId));

    this.deleteMutation = this.addMutation(deleteConsolidatedForecastMutation(workspaceId, consolidationId));
    this.duplicateMutation = this.addMutation(createConsolidatedForecastMutation(workspaceId, consolidationId));
    this.updateMutation = this.addMutation(updateConsolidatedForecastMutation(workspaceId, consolidationId));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public deleteForecast() {
    return this.deleteMutation.mutate(this.forecastId);
  }

  public duplicateForecast(data: ConsolidatedForecastCreateData) {
    return this.duplicateMutation.mutate(data);
  }

  public publishForecast() {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data: { status: 'published' } });
  }

  public resetMutations() {
    this.deleteMutation.reset();
    this.duplicateMutation.reset();
    this.updateMutation.reset();
    this.updateState();
  }

  public unpublishForecast() {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data: { status: 'draft' } });
  }

  public updateForecast(data: ConsolidatedForecastUpdateData) {
    return this.updateMutation.mutate({ forecastId: this.forecastId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get errorDeleting() {
    return this.deleteMutation.getCurrentResult().error;
  }

  private get errorDuplicating() {
    return this.duplicateMutation.getCurrentResult().error;
  }

  private get errorUpdating() {
    return this.updateMutation.getCurrentResult().error;
  }

  private get forecast() {
    const forecasts = this.getResult(this.consolidatedForecastsObs).data ?? [];
    return forecasts.find(f => f.id === this.forecastId);
  }

  protected getNewState(): ModifyConsolidatedForecastViewState {
    return {
      ...super.getNewState(),
      errorDeleting: this.errorDeleting,
      errorDuplicating: this.errorDuplicating,
      errorUpdating: this.errorUpdating,
      forecast: this.forecast,
      isDeleting: this.isDeleting,
      isDuplicating: this.isDuplicating,
      isUpdating: this.isUpdating,
      newForecastId: this.newForecastId
    };
  }

  private get isDeleting() {
    return this.deleteMutation.getCurrentResult().status === 'pending';
  }

  private get isDuplicating() {
    return this.duplicateMutation.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.updateMutation.getCurrentResult().status === 'pending';
  }

  private get newForecastId() {
    return this.duplicateMutation.getCurrentResult().data?.id;
  }
}
